import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { 
  User,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  getIdToken,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { logAuthState } from '@/lib/debugHelper';

interface AuthContextProps {
  currentUser: User | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (email: string, password: string, name: string) => Promise<User>;
  logOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<User>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  refreshToken: () => Promise<string | null>;
}

interface UserProfile {
  displayName: string;
  email: string;
  photoURL?: string;
  bio?: string;
  createdAt?: unknown;
  lastActive?: unknown;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Debug authentication state
      console.log('Auth state changed:', user ? `User: ${user.uid}` : 'No user');
      if (user) {
        logAuthState(user);
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function signIn(email: string, password: string): Promise<User> {
    const result = await signInWithEmailAndPassword(auth, email, password);

    try {
      const userRef = doc(db, 'users', result.user.uid);
      await setDoc(userRef, { lastActive: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.warn('Failed to update last active after sign-in:', err);
    }

    setCurrentUser(result.user);
    return result.user;
  }

  async function signUp(email: string, password: string, name: string): Promise<User> {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Create user profile in Firestore
    const userRef = doc(db, 'users', result.user.uid);
    await setDoc(userRef, {
      displayName: name, // Save as displayName for consistency with settings page
      email,
      createdAt: serverTimestamp(),
      lastActive: serverTimestamp(),
    });

    setCurrentUser(result.user);
    return result.user;
  }

  async function logOut(): Promise<void> {
    if (currentUser) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, { lastActive: serverTimestamp() }, { merge: true });
      } catch (err) {
        console.warn('Failed to update last active before sign-out:', err);
      }
    }
    return firebaseSignOut(auth);
  }

  async function resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(auth, email);
  }

  async function signInWithGoogle(): Promise<User> {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);

    // Check if this is a new user and create profile if needed
    const userRef = doc(db, 'users', result.user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      await setDoc(userRef, {
        displayName: result.user.displayName, // Save as displayName for consistency
        email: result.user.email,
        photoURL: result.user.photoURL,
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
      });
    } else {
      await setDoc(userRef, { lastActive: serverTimestamp() }, { merge: true });
    }

    setCurrentUser(result.user);
    return result.user;
  }

  async function updateUserProfile(data: Partial<UserProfile>): Promise<void> {
    if (!currentUser) throw new Error("No authenticated user");
    
    const userRef = doc(db, 'users', currentUser.uid);
    return setDoc(userRef, { ...data, lastActive: serverTimestamp() }, { merge: true });
  }
  
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!currentUser) return null;

    try {
      const token = await getIdToken(currentUser, false);

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, { lastActive: serverTimestamp() }, { merge: true });
      } catch (err) {
        console.warn('Failed to update last active timestamp:', err);
        // Continue anyway as this is not critical
      }

      return token as string;
    } catch (error) {
      console.error('Error getting auth token:', error);

      return null;
    }
  }, [currentUser]);

  const value = {
    currentUser,
    user: currentUser,
    loading,
    signIn,
    signUp,
    logOut,
    resetPassword,
    signInWithGoogle,
    updateUserProfile,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
